import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import { ExternalFilter, FiltersUtils } from 'dsilo-ui-components';

import AdminEditableTable from './components/AdminEditableTable';
import useStyles from './style';
import {
    isIdmApiEnabled,
    resolveBehaviorConfig,
    shouldShowTableLoader,
} from './config/resolveBehaviorConfig';
import { isConfigEnabled } from './config/isConfigEnabled';
import {
    buildIdmCellSavePayload,
    getBelowTrackerThresholdError,
    getIdmTierRestrictionMessage,
    getTierControlField,
    isFieldEditableForRowTier,
    shouldBlockIdmTriggerFieldSave,
} from './idmTierRules';
import { getNegotiationStatusAutoUpdatePayload, getManualNegotiationStatusValidationError } from './rules/negotiationStatusRules';
import {
    buildFieldHistoryAppendPayload,
    buildFieldsFromSelectedElements,
    enrichColumnHeaders,
    getRequiredFieldValidationMessage,
    isColumnRequired,
    isRequiredFieldValueEmpty,
    mergeTableConditions,
    normalizeChartSort,
    resolveColumnConfig,
    validateTierReviewDates,
} from './utils';

const DATA_SUFFIX = '_adminEditableListData';
const UPDATE_SUFFIX = '_adminEditableListUpdate';
const TIER_RESET_SUFFIX = '_save_deal_review_history';

const AdminEditableListIDMView = (props) => {
    const { data, formData, appId, orgId, loader } = props;
    const { chartData, chartUniqueId } = data || {};
    const selectedDataModel = chartData?.selectedDataModel;
    const chartConditions = chartData?.conditions;
    const externalFilter = chartData?.externalFilter;
    const paginationConfig = chartData?.pagination;
    const selectedDataElement = chartData?.selectedDataElement;
    const hasCheckbox = Boolean(
        chartData?.checkbox
        || chartData?.behavior?.features?.rowSelection?.isEnable,
    );
    const pageTitle = chartData?.title || chartData?.name;

    const behaviorConfig = useMemo(
        () => resolveBehaviorConfig(chartData),
        [chartData],
    );

    const historyDataModelId = behaviorConfig.api.historyDataModelId;
    const historySubDataModelId = behaviorConfig.api.subDataModelId;
    const tierResetConfig = behaviorConfig.tierResetLegacy;
    const isIDMEnable = isIdmApiEnabled(behaviorConfig);

    const dataUniqueId = `${chartUniqueId}${DATA_SUFFIX}`;
    const updateDataUniqueId = `${chartUniqueId}${UPDATE_SUFFIX}`;
    const tierResetDataUniqueId = `${chartUniqueId}${TIER_RESET_SUFFIX}`;
    const externalFilterKey = `${chartUniqueId}_externalFilter`;

    const [blockHeaders, setBlockHeaders] = useState([]);
    const [filterConditions, setFilterConditions] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(paginationConfig?.rowsPerPage || 10);
    const [sort, setSort] = useState({});
    const [resettingRowId, setResettingRowId] = useState(null);
    const [rowPatchesById, setRowPatchesById] = useState({});

    const wasSavingRef = useRef(false);
    const wasTierResettingRef = useRef(false);
    const filterOptionsRequestedRef = useRef(false);
    const classes = useStyles();

    const fields = useMemo(
        () => buildFieldsFromSelectedElements(selectedDataElement || [], behaviorConfig),
        [selectedDataElement, behaviorConfig],
    );

    const defaultSort = useMemo(
        () => normalizeChartSort(chartData?.sort),
        [chartData?.sort],
    );

    const tableResponse = formData?.document?.[dataUniqueId];
    const tableRows = tableResponse?.data || [];
    const displayRows = useMemo(() => {
        if (!isConfigEnabled(behaviorConfig.features.optimisticRowUpdateOnSave)) {
            return tableRows;
        }

        const patchIds = Object.keys(rowPatchesById);
        if (!patchIds.length) {
            return tableRows;
        }

        return tableRows.map((row) => {
            const patch = rowPatchesById[row._id];
            if (!patch) {
                return row;
            }

            if (patch.updatedDate && row.updatedDate === patch.updatedDate) {
                return row;
            }

            return { ...row, ...patch };
        });
    }, [tableRows, rowPatchesById, behaviorConfig.features.optimisticRowUpdateOnSave]);
    const totalRecords = tableResponse?.totalRecords ?? tableResponse?.total ?? tableRows.length;
    const isTableLoading = Boolean(formData?.[`${dataUniqueId}_loading`]);
    const isRowSaving = Boolean(formData?.[`${updateDataUniqueId}_loading`]);
    const isTierResetLoading = Boolean(formData?.[`${tierResetDataUniqueId}_loading`]);

    const fetchTableData = (
        nextPage,
        nextRowsPerPage,
        activeFilterConditions = [],
        activeSort = sort,
        loaderTrigger = 'initialLoad',
    ) => {
        if (!selectedDataModel || !fields) {
            return;
        }

        const showLoader = shouldShowTableLoader(behaviorConfig, loaderTrigger);

        if (showLoader && formData?.[`${dataUniqueId}_loading`]) {
            return;
        }

        const payload = {
            appId,
            id: selectedDataModel,
            fields,
            conditions: mergeTableConditions(chartConditions || [], activeFilterConditions),
            dataUniqueId,
            page: nextPage,
            rowsPerPage: nextRowsPerPage,
        };

        if (!showLoader) {
            payload.localLoading = true;
        }

        if (activeSort && Object.keys(activeSort).length > 0) {
            payload.sort = activeSort;
        }

        if (isIDMEnable) {
            payload.isIDMEnabled = true;
        }

        props.getObligationTableData(payload);
    };

    const fetchExternalFilterOptions = () => {
        if (!externalFilter?.length || !selectedDataModel) {
            return;
        }

        props.getModelDataDropDown({
            appId,
            id: selectedDataModel,
            fields: externalFilter.map((filter) => filter.value).join(','),
            page: 0,
            rowsPerPage: 'all',
            conditions: chartConditions || [],
            dataUniqueId: externalFilterKey,
        });
    };

    useEffect(() => {
        filterOptionsRequestedRef.current = false;
    }, [chartUniqueId]);

    useEffect(() => {
        if (!selectedDataModel) {
            return;
        }
        if (formData?.dataModel?.[selectedDataModel] || formData?.[`${selectedDataModel}_loading`]) {
            return;
        }

        props.getDataModelById({
            appid: appId,
            orgId,
            dataModelId: selectedDataModel,
            dataUniqueId: selectedDataModel,
        });
    }, [selectedDataModel, appId, orgId]);

    useEffect(() => {
        if (!externalFilter?.length || !selectedDataModel) {
            return;
        }
        if (formData?.externalFilterData?.[externalFilterKey] || filterOptionsRequestedRef.current) {
            return;
        }

        filterOptionsRequestedRef.current = true;
        fetchExternalFilterOptions();
    }, [selectedDataModel, chartUniqueId, externalFilter, appId]);

    useEffect(() => {
        if (!selectedDataModel || !fields) {
            return;
        }

        setPage(0);
        setFilterConditions([]);
        setFilterValues({});
        setSort(defaultSort);
        setRowPatchesById({});
        fetchTableData(0, rowsPerPage, [], defaultSort, 'initialLoad');
    }, [selectedDataModel, chartUniqueId, fields, defaultSort]);

    useEffect(() => {
        setRowPatchesById({});
    }, [tableResponse]);

    useEffect(() => {
        if (wasSavingRef.current && !isRowSaving) {
            const saveFeedback = behaviorConfig.table.saveFeedback;
            const updateError = formData?.error?.[updateDataUniqueId];

            if (updateError != null && updateError !== '') {
                if (isConfigEnabled(saveFeedback) && isConfigEnabled(saveFeedback.errorToast)) {
                    toast.error(
                        typeof updateError === 'string'
                            ? updateError
                            : saveFeedback.errorToast.message,
                    );
                }
                props.clearReduxDataOfCurrentComponent?.({
                    key: 'error',
                    subKey: [updateDataUniqueId],
                });
            } else {
                const updateResponse = formData?.document?.[updateDataUniqueId];

                if (updateResponse?._id && isConfigEnabled(behaviorConfig.features.optimisticRowUpdateOnSave)) {
                    setRowPatchesById((prev) => ({
                        ...prev,
                        [updateResponse._id]: updateResponse,
                    }));
                }

                if (isConfigEnabled(saveFeedback) && isConfigEnabled(saveFeedback.successToast)) {
                    const successMessage = updateResponse?.message || saveFeedback.successToast.message;
                    toast.success(successMessage);
                }

                const loaderTrigger = isConfigEnabled(behaviorConfig.features.silentRefreshOnSave)
                    ? 'cellSave'
                    : 'initialLoad';
                fetchTableData(page, rowsPerPage, filterConditions, sort, loaderTrigger);
                fetchExternalFilterOptions();

                props.clearReduxDataOfCurrentComponent?.({
                    key: 'document',
                    subKey: [updateDataUniqueId],
                });
            }
        }
        wasSavingRef.current = isRowSaving;
    }, [isRowSaving]);

    useEffect(() => {
        if (wasTierResettingRef.current && !isTierResetLoading) {
            const saveDealReviewPayload = formData?.document?.[tierResetDataUniqueId];
            if (saveDealReviewPayload) {
                const errorMessage = saveDealReviewPayload?.error;
                if (errorMessage != null && errorMessage !== '') {
                    toast.error(
                        typeof errorMessage === 'string' ? errorMessage : 'Reset failed',
                    );
                } else {
                    const successMessage = saveDealReviewPayload?.message
                        || saveDealReviewPayload?.data?.message
                        || behaviorConfig.tier.reset.successMessage;
                    toast.success(successMessage);
                    fetchTableData(page, rowsPerPage, filterConditions, sort, 'tierReset');
                    fetchExternalFilterOptions();
                }

                props.clearReduxDataOfCurrentComponent?.({
                    key: 'document',
                    subKey: [tierResetDataUniqueId],
                });
            }
            setResettingRowId(null);
        }
        wasTierResettingRef.current = isTierResetLoading;
    }, [isTierResetLoading]);

    useEffect(() => {
        if (!selectedDataElement?.length) {
            setBlockHeaders([]);
            return;
        }

        const dataElements = formData?.dataModel?.[selectedDataModel]?.dataElements || [];
        setBlockHeaders(enrichColumnHeaders(selectedDataElement, dataElements, behaviorConfig));
    }, [formData?.dataModel?.[selectedDataModel], selectedDataModel, selectedDataElement, behaviorConfig]);

    const handleExternalFilterChange = (nextFilterValues) => {
        const nextConditions = FiltersUtils.createConditions(nextFilterValues, externalFilter || []);
        setFilterValues(nextFilterValues);
        setFilterConditions(nextConditions);
        setPage(0);
        fetchTableData(0, rowsPerPage, nextConditions, sort, 'filterChange');
    };

    const handlePageChange = (_event, nextPage) => {
        setPage(nextPage);
        fetchTableData(nextPage, rowsPerPage, filterConditions, sort, 'pagination');
    };

    const handleRowsPerPageChange = (event) => {
        const nextRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(nextRowsPerPage);
        setPage(0);
        fetchTableData(0, nextRowsPerPage, filterConditions, sort, 'rowsPerPageChange');
    };

    const handleSortChange = (nextSort) => {
        setSort(nextSort);
        setPage(0);
        fetchTableData(0, rowsPerPage, filterConditions, nextSort, 'sortChange');
    };

    const handleSaveCell = (row, fieldKey, value) => {
        if (!row?._id || !selectedDataModel || isRowSaving) {
            return;
        }

        if (!isConfigEnabled(behaviorConfig.features.inlineEdit)) {
            return;
        }

        const column = resolveColumnConfig(fieldKey, blockHeaders, selectedDataElement || []);

        if (!isFieldEditableForRowTier(fieldKey, column, row, selectedDataElement || [], behaviorConfig)) {
            toast.info(getIdmTierRestrictionMessage(column, row, selectedDataElement || [], behaviorConfig));
            return;
        }

        if (column && isColumnRequired(column, behaviorConfig) && isRequiredFieldValueEmpty(value, column)) {
            toast.error(getRequiredFieldValidationMessage(column));
            return;
        }

        const manualStatusError = getManualNegotiationStatusValidationError(
            fieldKey,
            value,
            row,
            behaviorConfig,
        );
        if (manualStatusError) {
            toast.error(manualStatusError);
            return;
        }

        if (shouldBlockIdmTriggerFieldSave(row, fieldKey, behaviorConfig)) {
            const thresholdError = getBelowTrackerThresholdError(row, behaviorConfig);
            if (thresholdError) {
                toast.error(
                    <>
                        <strong>{thresholdError.title}</strong>
                        <div>{thresholdError.message}</div>
                    </>,
                    { toastId: 'idm-below-tracker-threshold' },
                );
            }
            return;
        }

        const dataElements = formData?.dataModel?.[selectedDataModel]?.dataElements || [];
        const payload = isIDMEnable
            ? buildIdmCellSavePayload(
                row,
                fieldKey,
                value,
                dataElements,
                selectedDataElement || [],
                behaviorConfig,
            )
            : { [fieldKey]: value };

        const mergedRow = { ...row, ...payload };

        // error mode blocks save; warning mode shows persistent inline messages in the table only
        const dateValidationMessage = validateTierReviewDates(mergedRow, behaviorConfig);
        if (dateValidationMessage) {
            toast.error(dateValidationMessage);
            return;
        }
        const controlField = getTierControlField(selectedDataElement || [], behaviorConfig);

        Object.assign(
            payload,
            getNegotiationStatusAutoUpdatePayload(fieldKey, value, mergedRow, behaviorConfig),
        );

        if (mergedRow[controlField] !== row[controlField]) {
            Object.assign(
                payload,
                getNegotiationStatusAutoUpdatePayload(
                    controlField,
                    mergedRow[controlField],
                    mergedRow,
                    behaviorConfig,
                ),
            );
        }

        Object.assign(
            payload,
            buildFieldHistoryAppendPayload(
                row,
                column,
                value,
                props.members?.name || 'Unknown',
                behaviorConfig,
            ),
        );

        props.updateOpportunity({
            appId,
            dataModelId: selectedDataModel,
            dataUniqueId: updateDataUniqueId,
            opportunityId: row._id,
            payload,
        });
    };

    const handleTierReset = (row) => {
        const documentId = row?._id;
        if (!documentId || isTierResetLoading || !isConfigEnabled(behaviorConfig.tier.reset)) {
            return;
        }

        if (!historyDataModelId || !historySubDataModelId) {
            toast.error('Tier reset is not configured for this list.');
            return;
        }

        props.saveDealReviewHistory({
            appId,
            dataModelId: historyDataModelId,
            subDataModelId: historySubDataModelId,
            dataUniqueId: tierResetDataUniqueId,
            documentId: String(documentId),
            payload: {
                pageUrl: props.page?.url,
            },
        });
        setResettingRowId(documentId);
    };

    const externalFilterData = formData?.externalFilterData?.[externalFilterKey]?.data
        || formData?.document?.[externalFilterKey]?.data
        || [];

    return (
        <div className={classes.container}>
            {pageTitle ? (
                <Typography className={classes.pageTitle} component="h2">
                    {pageTitle}
                </Typography>
            ) : null}
            {externalFilter?.length > 0 && (
                <div className={classes.filterBar}>
                    <ExternalFilter
                        {...props}
                        externalFilters={externalFilter}
                        externalFilterData={externalFilterData}
                        externalFilterValues={filterValues}
                        onExternalFilterChange={handleExternalFilterChange}
                        conditions={chartConditions || []}
                        isFetchAgain={true}
                    />
                </div>
            )}
            <AdminEditableTable
                {...props}
                rows={displayRows}
                blockHeaders={blockHeaders}
                loading={isTableLoading}
                loader={loader}
                page={page}
                rowsPerPage={rowsPerPage}
                totalRecords={totalRecords}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSaveCell={handleSaveCell}
                isRowSaving={isRowSaving}
                selectedDataModel={selectedDataModel}
                membersList={props.members?.data || props.members || []}
                hasCheckbox={hasCheckbox}
                selectedDataElement={selectedDataElement || []}
                sort={sort}
                onSortChange={handleSortChange}
                behaviorConfig={behaviorConfig}
                tierResetConfig={tierResetConfig}
                onTierReset={handleTierReset}
                resettingRowId={resettingRowId}
                isTierResetLoading={isTierResetLoading}
            />
        </div>
    );
};

export default AdminEditableListIDMView;
